import { useState } from "react";
import { Star, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { publicApi, errMsg } from "@/lib/api";

const LABELS = ["", "Very bad", "Not good", "Okay", "Good", "Excellent"];
const TAGS = ["Loved it", "Fresh & tasty", "Too sour", "Too thick", "Too watery", "Late delivery", "Leaked packet", "Wrong item"];

export const Stars = ({ value, size = "h-4 w-4", onPick, prefix }) => (
  <div className="flex gap-1">
    {[1, 2, 3, 4, 5].map((n) => {
      const icon = <Star className={`${size} ${n <= value ? "fill-[#F2B880] text-[#E0A060]" : "text-slate-300"}`} />;
      return onPick
        ? <button key={n} type="button" onClick={() => onPick(n)} className="transition-transform hover:scale-110" data-testid={`${prefix}-star-${n}`}>{icon}</button>
        : <span key={n}>{icon}</span>;
    })}
  </div>
);

export const RateDelivery = ({ token, order, onSaved }) => {
  const [v, setV] = useState({ stars: order.rating?.stars || 0, tags: order.rating?.tags || [], comment: order.rating?.comment || "" });
  const [editing, setEditing] = useState(!order.rating);
  const [busy, setBusy] = useState(false);
  const p = `rate-${order.id}`;
  if (!editing) return (
    <div className="flex items-center justify-between rounded-xl bg-[#FFF7EC] px-3 py-2" data-testid={`${p}-saved`}>
      <div><Stars value={order.rating.stars} /><div className="text-xs text-muted-foreground mt-1">{[...(order.rating.tags || []), order.rating.comment].filter(Boolean).join(" · ") || "Thanks for rating!"}</div></div>
      <button className="text-xs font-semibold text-[#0F382C] underline" onClick={() => setEditing(true)} data-testid={`${p}-edit`}>Change</button>
    </div>
  );
  const toggle = (t) => setV({ ...v, tags: v.tags.includes(t) ? v.tags.filter((x) => x !== t) : [...v.tags, t] });
  const save = async () => {
    setBusy(true);
    try {
      const r = await publicApi.post(`/${token}/deliveries/${order.id}/rating`, v);
      toast.success(r.data.winback ? `Sorry about that. ₹${r.data.winback} has been taken off your bill.` : "Thank you for your rating!");
      setEditing(false); onSaved();
    }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <div className="rounded-xl border border-[#F2B880] bg-[#FFF7EC] p-3 space-y-3" data-testid={`${p}-form`}>
      <div className="text-sm font-semibold text-[#0F382C]">How was this delivery?</div>
      <div className="flex items-center gap-3"><Stars value={v.stars} size="h-8 w-8" onPick={(n) => setV({ ...v, stars: n })} prefix={p} /><span className="text-sm text-muted-foreground">{LABELS[v.stars]}</span></div>
      {v.stars > 0 && <>
        <div className="flex flex-wrap gap-2">
          {TAGS.map((t, i) => (
            <button key={t} type="button" onClick={() => toggle(t)} className={`rounded-full border px-3 py-1 text-xs font-semibold transition-colors ${v.tags.includes(t) ? "border-[#0F382C] bg-[#0F382C] text-white" : "border-[#E7E2D6] bg-white text-[#0F382C]"}`} data-testid={`${p}-tag-${i}`}>{t}</button>
          ))}
        </div>
        <Textarea placeholder="Anything else? (optional)" value={v.comment} onChange={(e) => setV({ ...v, comment: e.target.value })} className="bg-white" data-testid={`${p}-comment`} />
        <Button disabled={busy} onClick={save} className="h-11 w-full rounded-xl bg-[#0F382C] hover:bg-[#134E3A]" data-testid={`${p}-submit`}>{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Submit rating"}</Button>
      </>}
    </div>
  );
};
